import { FC, useMemo, useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Column, CellProps } from 'react-table';
import { FaPrint } from 'react-icons/fa';

import { useTranslation } from 'react-i18next';
import FetchTable from 'components/table/FetchTable';
import { getUserInfo } from 'utils/auth';
import useWindowWidth from 'utils/helpers/useWindowWidth';
import TooltipWrapper from 'components/TooltipWrapper';
import Button from 'components/Button';
import Spinner from 'components/Spinner';
import handlePreviewDoc from 'utils/helpers/handlePreviewDoc';
import { IReport } from '../types';

interface IProps {
  reportParams: any;
  isDownloadMode: boolean;
}

const DocsTable: FC<IProps> = ({ reportParams, isDownloadMode }) => {
  const { t } = useTranslation();
  const isFeatureClientF = getUserInfo().featureCustomFeatureForClientF;

  const [link, setLink] = useState<string | null>(null);
  const [isLoadingDownload, setIsLoadingDownload] = useState(false);
  const openLinkBtn = useRef<HTMLAnchorElement>(null);
  const windowWidth = useWindowWidth();

  useEffect(() => {
    if (link) openLinkBtn?.current?.click();
  }, [link]);

  const columns: Array<Column<IReport>> = useMemo(() => {
    const baseColumns: Array<Column<IReport>> = [
      {
        Header: t('documents.table.documentID') || '',
        accessor: 'id',
        width: '3%',
        disableSortBy: true,
      },
      ...(isFeatureClientF
        ? [
            {
              Header: 'FlutterBe ID',
              accessor: 'flutterBe',
              disableSortBy: true,
              width: '3%',
            },
            {
              Header: 'Payroll ID',
              accessor: 'payrollId',
              disableSortBy: true,
              width: '3%',
            },
          ]
        : []),
      {
        Header: t('documents.table.name') || '',
        accessor: 'name',
        disableSortBy: true,
        width: windowWidth < 1600 ? '15%' : '8%',
      },
      {
        Header: t('documents.table.category') || '',
        accessor: 'category',
        disableSortBy: true,
        width: '5%',
      },
      {
        Header: t('documents.table.employee') || '',
        accessor: 'employee',
        disableSortBy: true,
        width: windowWidth < 1600 ? '15%' : '8%',
      },
      {
        Header: t('documents.table.group') || '',
        accessor: 'groups',
        disableSortBy: true,
        width: windowWidth < 1600 ? '15%' : '8%',
      },
      {
        Header: t('documents.table.author') || '',
        accessor: 'creator',
        disableSortBy: true,
        width: windowWidth < 1600 ? '15%' : '8%',
      },
      {
        Header: t('documents.table.createdAt') || '',
        accessor: 'created_on',
        disableSortBy: true,
        width: '3%',
      },
      {
        Header: t('documents.table.signedByEmployee') || '',
        accessor: 'signed_by_employee',
        disableSortBy: true,
        width: '3%',
      },
      {
        Header: t('documents.table.signedByEmployer') || '',
        accessor: 'signed_by_employer',
        disableSortBy: true,
        width: '3%',
      },
      {
        Header: t('documents.table.archivedOn') || '',
        accessor: 'archived_on',
        disableSortBy: true,
        width: '3%',
      },
      {
        Header: t('documents.table.documentHolder') || '',
        accessor: 'document_holder',
        disableSortBy: true,
        width: '3%',
      },
      {
        Header: () => <div style={{ textAlign: 'right' }}>{t('table.actions')}</div>,
        accessor: 'actions',
        disableSortBy: true,
        width: '3%',
        Cell: ({ row }: CellProps<IReport>) => (
          <div style={{ textAlign: 'right' }}>
            <TooltipWrapper
              id={`tooltip-print-${row.original.id}`}
              overlay={t('documents.table.preview')}
            >
              <Button
                type="button"
                btnType="dark"
                size="sm"
                outlined
                onClick={(e: any) => {
                  e.stopPropagation();
                  handlePreviewDoc({
                    id: row.original.id,
                    callback: (res: any) => setLink(res.data),
                    showModal: () => {},
                    setIsLoading: setIsLoadingDownload,
                  });
                }}
              >
                {!isLoadingDownload && <FaPrint size={14} />}
                {isLoadingDownload && <Spinner size={14} />}
              </Button>
            </TooltipWrapper>
          </div>
        ),
      },
    ];

    return baseColumns;
  }, [t, isFeatureClientF, isLoadingDownload]);

  return (
    <>
      <FetchTable<IReport>
        columns={columns}
        entityName="documents-report"
        entityEndpoint="reports/document-actions/json"
        requestType="POST"
        searchParams={reportParams}
        pageLimit={20}
        skipResetExpanded
        hideFilterBar
        isReport
        pagination
        showID
        isSelectable
        customSelectableRowProps={isDownloadMode ? ['CAN_MULTI_DOWNLOAD'] : undefined}
      />
      {link && (
        <Link
          ref={openLinkBtn}
          to={{ pathname: link }}
          target="_blank"
          style={{ display: 'none' }}
        />
      )}
    </>
  );
};

export default DocsTable;
